import { useState, useEffect, useMemo } from 'react';
import { X, Save, Loader2, Download, AlertCircle, CheckCircle, Eye, Code, Copy, Check, Clock } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ContentEditor.css';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// HTML to Markdown converter for download
const htmlToMarkdown = (html: string): string => {
  if (!html) return '';

  let markdown = html;

  // Remove style attributes and clean up
  markdown = markdown.replace(/\s*style="[^"]*"/gi, '');
  markdown = markdown.replace(/\s*class="[^"]*"/gi, '');

  // Convert headers
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n');
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n');
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n');
  markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n');
  markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n\n');
  markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');

  // Convert bold and italic
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
  markdown = markdown.replace(/<u[^>]*>(.*?)<\/u>/gi, '$1');
  markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~');
  markdown = markdown.replace(/<strike[^>]*>(.*?)<\/strike>/gi, '~~$1~~');

  // Convert links
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

  // Convert inline code
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');

  // Convert code blocks
  markdown = markdown.replace(/<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```\n\n');
  markdown = markdown.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, '```\n$1\n```\n\n');

  // Convert blockquotes
  markdown = markdown.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (match, content) => {
    const lines = content.replace(/<[^>]+>/g, '').split('\n');
    return lines.map((line: string) => `> ${line.trim()}`).join('\n') + '\n\n';
  });

  // Convert unordered lists
  markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, content) => {
    const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    return items.map((item: string) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim();
      return `- ${text}`;
    }).join('\n') + '\n\n';
  });

  // Convert ordered lists
  markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, content) => {
    const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    return items.map((item: string, index: number) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim();
      return `${index + 1}. ${text}`;
    }).join('\n') + '\n\n';
  });

  // Convert horizontal rules
  markdown = markdown.replace(/<hr[^>]*\/?>/gi, '\n---\n\n');

  // Convert paragraphs
  markdown = markdown.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1\n\n');

  // Convert line breaks
  markdown = markdown.replace(/<br[^>]*\/?>/gi, '\n');

  // Remove remaining HTML tags
  markdown = markdown.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  markdown = markdown.replace(/&amp;/g, '&');
  markdown = markdown.replace(/&lt;/g, '<');
  markdown = markdown.replace(/&gt;/g, '>');
  markdown = markdown.replace(/&nbsp;/g, ' ');
  markdown = markdown.replace(/&quot;/g, '"');
  markdown = markdown.replace(/&#39;/g, "'");
  markdown = markdown.replace(/&rsquo;/g, "'");
  markdown = markdown.replace(/&lsquo;/g, "'");
  markdown = markdown.replace(/&rdquo;/g, '"');
  markdown = markdown.replace(/&ldquo;/g, '"');
  markdown = markdown.replace(/&mdash;/g, '—');
  markdown = markdown.replace(/&ndash;/g, '–');
  markdown = markdown.replace(/&hellip;/g, '...');

  // Clean up excessive whitespace
  markdown = markdown.replace(/\n{3,}/g, '\n\n');
  markdown = markdown.trim();

  return markdown;
};

// Markdown to HTML converter
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';

  let html = markdown;

  // Escape HTML special characters
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Process line by line
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  let listType = '';

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    if (line.trim() === '') {
      if (inList) {
        processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
        listType = '';
      }
      processedLines.push('');
      continue;
    }

    // Headers
    const h6Match = line.match(/^######\s+(.+)$/);
    const h5Match = line.match(/^#####\s+(.+)$/);
    const h4Match = line.match(/^####\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    const h2Match = line.match(/^##\s+(.+)$/);
    const h1Match = line.match(/^#\s+(.+)$/);

    if (h6Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h6>${h6Match[1].trim()}</h6>`);
      continue;
    }
    if (h5Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h5>${h5Match[1].trim()}</h5>`);
      continue;
    }
    if (h4Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h4>${h4Match[1].trim()}</h4>`);
      continue;
    }
    if (h3Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h3>${h3Match[1].trim()}</h3>`);
      continue;
    }
    if (h2Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h2>${h2Match[1].trim()}</h2>`);
      continue;
    }
    if (h1Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h1>${h1Match[1].trim()}</h1>`);
      continue;
    }

    // Horizontal rules
    if (/^[-*_]{3,}\s*$/.test(line)) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push('<hr>');
      continue;
    }

    // Unordered list items
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (ulMatch && ulMatch[2].trim()) {
      if (!inList || listType !== 'ul') {
        if (inList) processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        processedLines.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      processedLines.push(`<li>${ulMatch[2].trim()}</li>`);
      continue;
    }

    // Ordered list items
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (olMatch && olMatch[3].trim()) {
      if (!inList || listType !== 'ol') {
        if (inList) processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        processedLines.push('<ol>');
        inList = true;
        listType = 'ol';
      }
      processedLines.push(`<li>${olMatch[3].trim()}</li>`);
      continue;
    }

    // Close list if we hit a regular line
    if (inList) {
      processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
      inList = false;
      listType = '';
    }

    processedLines.push(line);
  }

  if (inList) {
    processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
  }

  html = processedLines.join('\n');

  // Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  html = html.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Group non-block lines into paragraphs
  const finalLines = html.split('\n');
  const result: string[] = [];
  let paragraphLines: string[] = [];

  const isBlockElement = (line: string) => {
    return line.startsWith('<h') || line.startsWith('<ul') || line.startsWith('</ul') ||
           line.startsWith('<ol') || line.startsWith('</ol') || line.startsWith('<li') ||
           line.startsWith('<blockquote') || line.startsWith('<pre') ||
           line.startsWith('<hr') || line.trim() === '';
  };

  for (const line of finalLines) {
    if (isBlockElement(line)) {
      if (paragraphLines.length > 0) {
        const pContent = paragraphLines.join('<br>').trim();
        if (pContent) {
          result.push(`<p>${pContent}</p>`);
        }
        paragraphLines = [];
      }
      if (line.trim()) {
        result.push(line);
      }
    } else {
      paragraphLines.push(line);
    }
  }

  if (paragraphLines.length > 0) {
    const pContent = paragraphLines.join('<br>').trim();
    if (pContent) {
      result.push(`<p>${pContent}</p>`);
    }
  }

  html = result.join('\n');
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<li>\s*<\/li>/g, '');
  html = html.replace(/<ul>\s*<\/ul>/g, '');
  html = html.replace(/<ol>\s*<\/ol>/g, '');

  return html;
};

interface BriefContentEditorModalProps {
  pageId: number;
  pageTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export default function BriefContentEditorModal({
  pageId,
  pageTitle,
  isOpen,
  onClose,
  onSave
}: BriefContentEditorModalProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [copied, setCopied] = useState(false);
  const [editedAt, setEditedAt] = useState<string | null>(null);
  const [editedByName, setEditedByName] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [qaScore, setQaScore] = useState<number | null>(null);
  const [qaPassed, setQaPassed] = useState<boolean | null>(null);

  // WordPress-style rich text editor configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'script',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'color', 'background',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen, pageId]);

  useEffect(() => {
    const stripHtml = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    const textOnly = stripHtml(content);
    const words = textOnly.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
    setHasChanges(content !== originalContent);
  }, [content, originalContent]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/briefcontent/pages/${pageId}/content`);
      const data = response.data;

      if (data.content) {
        const htmlContent = markdownToHtml(data.content);
        setContent(htmlContent);
        setOriginalContent(htmlContent);
        setWordCount(data.word_count || 0);
        setEditedAt(data.edited_at);
        setEditedByName(data.edited_by_name);
        setGeneratedAt(data.generated_at);
        setQaScore(data.qa_score);
        setQaPassed(data.qa_passed);
      } else {
        throw new Error('Page content not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSaveSuccess(false);

      const markdownContent = htmlToMarkdown(content);
      const formData = new FormData();
      formData.append('content', markdownContent);
      if (user?.name || user?.email) {
        formData.append('edited_by_name', user.name || user.email);
      }

      await api.put(`/briefcontent/pages/${pageId}/content`, formData);
      setOriginalContent(content);
      setHasChanges(false);
      setSaveSuccess(true);
      setEditedAt(new Date().toISOString());
      setEditedByName(user?.name || user?.email || null);

      setTimeout(() => setSaveSuccess(false), 3000);

      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (format: 'md' | 'txt' = 'md') => {
    const markdownContent = htmlToMarkdown(content);
    const blob = new Blob([markdownContent], { type: format === 'md' ? 'text/markdown; charset=utf-8' : 'text/plain; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pageTitle.toLowerCase().replace(/\s+/g, '_')}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    try {
      const markdownContent = htmlToMarkdown(content);
      await navigator.clipboard.writeText(markdownContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {pageTitle}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {wordCount.toLocaleString()} words
                {qaScore !== null && (
                  <span className={`ml-2 ${qaPassed ? 'text-green-600' : 'text-orange-600'}`}>
                    • QA: {qaScore}%
                  </span>
                )}
                {hasChanges && <span className="ml-2 text-orange-600">• Unsaved changes</span>}
              </p>
              {/* Edit Info */}
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                {editedAt && editedByName ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last edited {new Date(editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })} by <span className="font-medium text-gray-600">{editedByName}</span>
                  </span>
                ) : editedAt ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last edited {new Date(editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                ) : generatedAt ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Generated {new Date(generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('edit')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'edit'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Edit Mode"
                >
                  <Code className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'preview'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Preview Mode"
                >
                  <Eye className="h-4 w-4 inline mr-1" />
                  Preview
                </button>
              </div>

              {/* Copy Button */}
              <button
                onClick={handleCopyToClipboard}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy to Clipboard (for Google Docs)"
              >
                {copied ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <Copy className="h-5 w-5" />
                )}
              </button>

              {/* Download Dropdown */}
              <div className="relative group">
                <button
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="h-5 w-5" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  <button
                    onClick={() => handleDownload('md')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg"
                  >
                    Download as .md
                  </button>
                  <button
                    onClick={() => handleDownload('txt')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg"
                  >
                    Download as .txt
                  </button>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="btn-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-3 text-gray-600">Loading content...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <p className="text-red-700 font-medium">{error}</p>
                  <button
                    onClick={loadContent}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : viewMode === 'edit' ? (
              <div className="h-full">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={modules}
                  formats={formats}
                  placeholder="Start writing your content here..."
                  className="h-[calc(100%-42px)]"
                  style={{ height: 'calc(100% - 42px)' }}
                />
              </div>
            ) : (
              <div className="h-full overflow-auto">
                <div className="prose prose-lg max-w-none bg-white p-8 rounded-lg border border-gray-200 document-preview">
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span>Word count: {wordCount.toLocaleString()}</span>
                <span className="mx-3">|</span>
                <span>Characters: {content.length.toLocaleString()}</span>
              </div>

              {saveSuccess && (
                <div className="flex items-center text-green-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Saved successfully!</span>
                </div>
              )}

              {error && !loading && (
                <div className="flex items-center text-red-700">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
